// Copyright (c) 2025 Goffy. All Rights Reserved. This file belongs to Goffy.

import { useEffect, useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet } from 'react-native';
import { Redirect, useLocalSearchParams, useRouter, useRootNavigationState } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';

import { Text, View } from '@/components/Themed';
import { t } from '@/i18n';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { setCurrentHole, setCurrentRoundId } from '@/services/currentRound';
import { setCurrentTourId } from '@/services/currentTour';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { useRoundState } from '@/stores/roundState';
import { getSubgameDisplayName } from '@/features/game-definitions/i18n';

type Player = {
  id: string;
  name: string;
};

type Subgame = {
  id: string;
  name: string;
  description?: string;
};

type Hole = {
  number: number;
  par?: number;
  strokeindex?: number;
};

type RoundMeta = {
  holesCount?: number;
  HolesCount?: number;
};

type RoundSubgameEntry = {
  playerId: string;
  holeNumber: number;
  subgameId: string;
};

type SelectionState = {
  holeNumber?: number;
  subgameId?: string;
};

export default function MysteryPrizeScreen() {
  const { roundId: roundIdParam, tourId, teamGame: teamGameParam, debug: debugParam } = useLocalSearchParams<{
    roundId?: string;
    tourId?: string;
    teamGame?: string;
    debug?: string;
  }>();
  const storedRoundId = useRoundState((state) => state.roundId);
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const roundIdFromParams = useMemo(() => {
    const raw = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
    return raw ?? null;
  }, [roundIdParam]);
  const roundId = roundIdFromParams ?? storedRoundId;
  const teamGame = teamGameParam === '1' || teamGameParam === 'true';
  const debug = debugParam === 'true';

  const [players, setPlayers] = useState<Player[]>([]);
  const [holes, setHoles] = useState<Hole[]>([]);
  const [subgames, setSubgames] = useState<Subgame[]>([]);
  const [selections, setSelections] = useState<Record<string, SelectionState>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState<'hole' | 'subgame' | null>(null);
  const [pickerPlayerId, setPickerPlayerId] = useState<string | null>(null);

  useEffect(() => {
    if (tourId) {
      setCurrentTourId(tourId);
    }
  }, [tourId]);

  useEffect(() => {
    if (roundId) {
      setCurrentRoundId(roundId);
    }
  }, [roundId]);

  useEffect(() => {
    if (roundIdFromParams && roundIdFromParams !== storedRoundId) {
      setCurrentRoundId(roundIdFromParams);
    }
  }, [roundIdFromParams, storedRoundId]);

  useEffect(() => {
    if (!isNavigationReady || !isFocused) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setSubscriptionContext({ roundId });
  }, [apiBaseUrl, isFocused, isNavigationReady, roundId]);

  useEffect(() => {
    if (!isNavigationReady || !roundId) return;
    const loadData = async () => {
      try {
        setLoading(true);
        const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : undefined;
        const [playersRes, roundRes, subgamesRes, selectionsRes] = await Promise.all([
          fetch(`${apiBaseUrl}/api/rounds/${roundId}/players`, { headers }),
          fetch(`${apiBaseUrl}/api/rounds/${roundId}`, { headers }),
          fetch(`${apiBaseUrl}/api/subgames`, { headers }),
          fetch(`${apiBaseUrl}/api/rounds/${roundId}/subgames`, { headers }),
        ]);

        if (!playersRes.ok || !roundRes.ok || !subgamesRes.ok || !selectionsRes.ok) {
          throw new Error(
            `${t('mysteryPrize.loadError')} ` +
            `(players=${playersRes.status}, round=${roundRes.status}, subgames=${subgamesRes.status}, selections=${selectionsRes.status})`
          );
        }

        const playersPayload = (await playersRes.json()) as { players?: Player[]; Players?: Player[] };
        const roundPayload = (await roundRes.json()) as RoundMeta;
        const subgamesPayload = (await subgamesRes.json()) as Subgame[];
        const selectionsPayload = (await selectionsRes.json()) as RoundSubgameEntry[];
        const holesCount = Number(roundPayload.holesCount ?? roundPayload.HolesCount ?? 0);
        const derivedHoles = Number.isFinite(holesCount) && holesCount > 0
          ? Array.from({ length: holesCount }, (_, index) => ({ number: index + 1 }))
          : [];

        const nextSelections: Record<string, SelectionState> = {};
        selectionsPayload.forEach((entry) => {
          nextSelections[entry.playerId] = {
            holeNumber: entry.holeNumber,
            subgameId: entry.subgameId,
          };
        });

        setPlayers(playersPayload.players ?? playersPayload.Players ?? []);
        setHoles(derivedHoles);
        setSubgames(subgamesPayload ?? []);
        setSelections(nextSelections);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('mysteryPrize.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [apiBaseUrl, isNavigationReady, roundId, user?.token]);

  if (!user) {
    return <Redirect href="/login" />;
  }

  if (!roundId) {
    return (
      <View style={styles.errorPage}>
        <Text style={styles.errorText}>{t('mysteryPrize.loadError')}</Text>
      </View>
    );
  }

  const openPicker = (playerId: string, mode: 'hole' | 'subgame') => {
    setPickerPlayerId(playerId);
    setPickerMode(mode);
    setPickerOpen(true);
  };

  const closePicker = () => {
    setPickerOpen(false);
    setPickerMode(null);
    setPickerPlayerId(null);
  };

  const updateSelection = (playerId: string, next: SelectionState) => {
    setSelections((prev) => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        ...next,
      },
    }));
  };

  const handleSave = () => {
    if (!roundId) return;
    const payloadSelections = players
      .map((player) => ({
        playerId: player.id,
        holeNumber: selections[player.id]?.holeNumber,
        subgameId: selections[player.id]?.subgameId,
      }))
      .filter((entry) => entry.holeNumber && entry.subgameId);
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    setSaving(true);
    socket.send({
      type: 'round.subgames.update',
      roundId,
      selections: payloadSelections.map(({ playerId, holeNumber, subgameId }) => ({
        playerId,
        holeNumber,
        subgameId,
      })),
    });

    const params: string[] = [];
    if (debug) params.push('debug=true');
    if (!roundId) return;
    if (teamGame) {
      const query = params.length ? `?${params.join('&')}` : '';
      router.replace(`/round/${roundId}/team-select${query}`);
    } else {
      setCurrentHole(1);
      if (tourId) params.push(`tourId=${tourId}`);
      const query = params.length ? `?${params.join('&')}` : '';
      router.replace(`/round/${roundId}/hole/1/compact${query}`);
    }
  };

  const holeOptions = holes
    .map((hole) => hole.number)
    .filter((num) => Number.isFinite(num))
    .sort((a, b) => a - b);
  const subgameById = subgames.reduce<Record<string, Subgame>>((acc, subgame) => {
    acc[subgame.id] = subgame;
    return acc;
  }, {});

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.title}>{t('mysteryPrize.title')}</Text>
      <Text style={styles.subtitle}>{t('mysteryPrize.subtitle')}</Text>
      {loading ? <Text style={styles.meta}>{t('mysteryPrize.loading')}</Text> : null}
      {error ? <Text style={styles.errorText}>{error}</Text> : null}
      {!loading && !error && players.length === 0 ? <Text style={styles.meta}>{t('mysteryPrize.noPlayers')}</Text> : null}
      {!loading && !error && subgames.length === 0 ? <Text style={styles.meta}>{t('mysteryPrize.noSubgames')}</Text> : null}
      <View style={styles.list}>
        {players.map((player) => {
          const selection = selections[player.id] ?? {};
          const holeLabel = selection.holeNumber ? `#${selection.holeNumber}` : t('mysteryPrize.selectHole');
          const subgameLabel = selection.subgameId
            ? getSubgameDisplayName(subgameById[selection.subgameId], t('mysteryPrize.selectSubgame'))
            : t('mysteryPrize.selectSubgame');
          return (
            <View key={player.id} style={styles.playerRow}>
              <Text style={styles.playerName}>{player.name}</Text>
              <View style={styles.choiceRow}>
                <Pressable style={styles.choiceButton} onPress={() => openPicker(player.id, 'hole')}>
                  <Text style={styles.choiceLabel}>{t('mysteryPrize.holeLabel')}</Text>
                  <Text style={styles.choiceValue}>{holeLabel}</Text>
                </Pressable>
                <Pressable style={styles.choiceButton} onPress={() => openPicker(player.id, 'subgame')}>
                  <Text style={styles.choiceLabel}>{t('mysteryPrize.subgameLabel')}</Text>
                  <Text style={styles.choiceValue}>{subgameLabel}</Text>
                </Pressable>
              </View>
            </View>
          );
        })}
      </View>
      <Pressable style={[styles.saveButton, saving && styles.saveButtonDisabled]} onPress={handleSave} disabled={saving}>
        <Text style={styles.saveButtonText}>{saving ? t('mysteryPrize.saving') : t('mysteryPrize.save')}</Text>
      </Pressable>

      <Modal visible={pickerOpen} transparent animationType="fade" onRequestClose={closePicker}>
        <Pressable style={styles.modalBackdrop} onPress={closePicker}>
          <Pressable style={styles.modalCard}>
            <Text style={styles.modalTitle}>
              {pickerMode === 'hole' ? t('mysteryPrize.holePickerTitle') : t('mysteryPrize.subgamePickerTitle')}
            </Text>
            <ScrollView>
              {(pickerMode === 'hole' ? holeOptions : subgames).map((option) => {
                if (pickerMode === 'hole') {
                  const holeNumber = option as number;
                  return (
                    <Pressable
                      key={`hole-${holeNumber}`}
                      style={styles.modalOption}
                      onPress={() => {
                        if (pickerPlayerId) {
                          updateSelection(pickerPlayerId, { holeNumber });
                        }
                        closePicker();
                      }}
                    >
                      <Text style={styles.modalOptionText}>{`#${holeNumber}`}</Text>
                    </Pressable>
                  );
                }

                const subgame = option as Subgame;
                return (
                  <Pressable
                    key={subgame.id}
                    style={styles.modalOption}
                    onPress={() => {
                      if (pickerPlayerId) {
                        updateSelection(pickerPlayerId, { subgameId: subgame.id });
                      }
                      closePicker();
                    }}
                  >
                    <Text style={styles.modalOptionText}>{getSubgameDisplayName(subgame, t('mysteryPrize.selectSubgame'))}</Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0b1330',
  },
  content: {
    padding: 20,
    gap: 14,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#f8f8f8',
  },
  subtitle: {
    fontSize: 14,
    color: '#c5d0ff',
  },
  meta: {
    color: '#c5d0ff',
    fontSize: 13,
  },
  errorText: {
    color: '#ff9a9a',
    fontSize: 13,
  },
  list: {
    gap: 12,
  },
  playerRow: {
    backgroundColor: 'rgba(23, 32, 64, 0.7)',
    borderRadius: 16,
    padding: 14,
    gap: 10,
  },
  playerName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  choiceRow: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  choiceButton: {
    flexGrow: 1,
    minWidth: 140,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 14,
    backgroundColor: 'rgba(12, 19, 40, 0.7)',
  },
  choiceLabel: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: '#8da0f5',
    marginBottom: 6,
  },
  choiceValue: {
    fontSize: 15,
    color: '#f8f8f8',
    fontWeight: '600',
  },
  saveButton: {
    paddingVertical: 14,
    borderRadius: 20,
    backgroundColor: '#2e61ff',
    alignItems: 'center',
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#ffffff',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalCard: {
    width: '100%',
    maxHeight: '70%',
    backgroundColor: '#0b1330',
    borderRadius: 20,
    padding: 16,
    gap: 10,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#ffffff',
  },
  modalOption: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
  },
  modalOptionText: {
    color: '#f8f8f8',
    fontSize: 14,
  },
  errorPage: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    backgroundColor: '#0b1330',
  },
});
